import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { debounce } from 'lodash';

import { AppDispatch } from 'app/store';

import { selectFilters } from 'features/filters/filtersSelectors';
import {
  setClearFilters,
  setFilters,
  updateFilterField,
  updateMultipleFilters
} from 'features/filters/filtersSlice';
import {
  selectModuleFilters,
  selectModuleFiltersLoading
} from 'features/moduleConfigs/moduleConfigsSelectors';
import {
  initializeModuleConfig,
  setActiveModule,
  setFiltersLoading,
  updateModuleFilters
} from 'features/moduleConfigs/moduleConfigsSlice';

import { cleanFilters, hasSignificantFilterChanges } from 'utils/filtersUtils';
import { getModuleSortConfig } from 'components/TableControls/sortOptions';

import { Filters } from 'types/filters';

import { AppModules, FilterFields, SidebarContentTypes } from 'constants/enums';
import { FILTERS_DEBOUNCE_TIMEOUT_MS } from 'constants/uiSettings';
import { DEFAULT_FILTERS } from 'constants/uiSettings';

interface UseFiltersReturn {
  filters: Filters;
  updateFilter: <T extends keyof Filters>(field: T, value: Filters[T]) => void;
  updateFilters: (updates: Partial<Filters>) => void;
  isLoading: boolean;
  resetFilters: () => void;
  isFilterLoadedAndReady: boolean;
}

interface UseFiltersOptions {
  debounceFilters?: number;
  autoSave?: boolean;
  autoClearOnUnmount?: boolean;
}

// Check if filters are ready to be applied
const isFiltersReady = (filters: Partial<Filters>): boolean => {
  return (
    filters[FilterFields.Page] !== null &&
    filters[FilterFields.PageSize] !== undefined
  );
};

// Map AppModules to SidebarContentTypes for sort config
const MODULE_TO_SIDEBAR_TYPE: Partial<Record<AppModules, SidebarContentTypes>> = {
  [AppModules.Jobs]: SidebarContentTypes.JobsFilters,
  [AppModules.Jobseekers]: SidebarContentTypes.JobseekersFilters,
  [AppModules.Applications]: SidebarContentTypes.ApplicationsFilters
};

export const useFilters = (
  module: AppModules,
  options: UseFiltersOptions = {}
): UseFiltersReturn => {
  const {
    debounceFilters = FILTERS_DEBOUNCE_TIMEOUT_MS,
    autoSave = true,
    autoClearOnUnmount = false
  } = options;

  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const currentFilters = useSelector(selectFilters);
  const savedFilters = useSelector(selectModuleFilters(module));
  const isLoading = useSelector(selectModuleFiltersLoading(module));

  const prevFiltersRef = useRef<Partial<Filters>>(currentFilters);
  const initializationDoneRef = useRef(false);

  // Calculate filters readiness
  const isFilterLoadedAndReady = useMemo(() => {
    return isFiltersReady(currentFilters) && !isLoading;
  }, [currentFilters, isLoading]);

  // Filters initialization
  useEffect(() => {
    if (initializationDoneRef.current) return;

    dispatch(initializeModuleConfig(module));
    dispatch(setActiveModule(module));

    // Set flag to avoid duplicate initialization
    initializationDoneRef.current = true;

    const filtersToRestore = savedFilters
      ? { ...DEFAULT_FILTERS, ...savedFilters }
      : DEFAULT_FILTERS;

    // Initialize default sort if sortField is missing
    if (filtersToRestore.sortField == null) {
      const sidebarType = MODULE_TO_SIDEBAR_TYPE[module];
      if (sidebarType) {
        const MODULE_SORT_CONFIG = getModuleSortConfig(t);
        let sortConfig;
        switch (sidebarType) {
          case SidebarContentTypes.JobsFilters:
            sortConfig = MODULE_SORT_CONFIG.jobs;
            break;
          case SidebarContentTypes.JobseekersFilters:
            sortConfig = MODULE_SORT_CONFIG.jobseekers;
            break;
          case SidebarContentTypes.ApplicationsFilters:
            sortConfig = MODULE_SORT_CONFIG.applications;
            break;
        }

        if (sortConfig?.defaultOption) {
          const defaultSort = sortConfig.options[sortConfig.defaultOption];
          if (defaultSort) {
            filtersToRestore.sortField = defaultSort.field;
            filtersToRestore.order = defaultSort.order ?? null;
          }
        }
      }
    }

    dispatch(setFilters(filtersToRestore));
    prevFiltersRef.current = filtersToRestore;
  }, [dispatch, module, savedFilters, t]);

  // Debounced function to save filters
  const debouncedSaveFilters = useCallback(
    debounce((filters: Partial<Filters>) => {
      if (!autoSave) return;

      const cleanedFilters = cleanFilters(filters);

      if (Object.keys(cleanedFilters).length > 0) {
        dispatch(
          updateModuleFilters({
            module,
            filters: cleanedFilters
          })
        );
      }

      dispatch(setFiltersLoading({ module, isLoading: false }));
    }, debounceFilters),
    [dispatch, module, autoSave, debounceFilters]
  );

  // Tracking changes of filters for auto-saving
  useEffect(() => {
    // Skip initializations and cases when initialization is not completed
    if (!initializationDoneRef.current) return;

    const hasChanges = hasSignificantFilterChanges(
      prevFiltersRef.current,
      currentFilters
    );

    if (hasChanges) {
      if (autoSave) {
        dispatch(setFiltersLoading({ module, isLoading: true }));
        debouncedSaveFilters(currentFilters);
      }
      prevFiltersRef.current = currentFilters;
    }
  }, [currentFilters, debouncedSaveFilters, module, autoSave, dispatch]);

  const updateFilter = useCallback(
    <T extends keyof Filters>(field: T, value: Filters[T]) => {
      dispatch(updateFilterField({ field, value }));
    },
    [dispatch]
  );

  const updateFilters = useCallback(
    (updates: Partial<Filters>) => {
      dispatch(updateMultipleFilters(updates));
    },
    [dispatch]
  );

  const resetFilters = useCallback(() => {
    dispatch(setClearFilters());
  }, [dispatch]);

  // Clean up debounced function and filters on unmount
  useEffect(() => {
    return () => {
      debouncedSaveFilters.cancel();
      if (autoClearOnUnmount) {
        dispatch(setClearFilters());
      }
    };
  }, [debouncedSaveFilters, dispatch, autoClearOnUnmount]);

  return {
    filters: currentFilters,
    updateFilter,
    updateFilters,
    isLoading,
    resetFilters,
    isFilterLoadedAndReady
  };
};
